import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { PdsOrdersGateway } from '../gateway';
import { resolveGateway } from '../resolve-gateway';
import { buildStockZones } from '../stock';
import type { PdsOrder } from '../types';
import { usePdsOrdersStore } from './orders.store';

export type StockDay =
  'today' | 'yesterday' | 'tomorrow' | 'last7' | 'next7' | 'custom';

const DAY_MS = 86_400_000;

const startOfDay = (time: number) => {
  const start = new Date(time);
  start.setHours(0, 0, 0, 0);
  return start.getTime();
};

// « AAAA-MM-JJ » d'un champ date, lu en heure locale ; vide ou illisible, `fallback`.
const dayOf = (value: string, fallback: number) => {
  const [year, month, day] = value.split('-').map(Number);
  const time = new Date(year, month - 1, day).getTime();
  return value && Number.isFinite(time) ? time : fallback;
};

// « HH:MM » en minutes depuis minuit ; vide ou illisible, pas de borne.
const minutesOf = (value: string) => {
  const [hours, minutes] = value.split(':').map(Number);
  return value && Number.isFinite(hours) && Number.isFinite(minutes)
    ? hours * 60 + minutes
    : null;
};

const dayBounds = (
  now: number,
  day: StockDay,
  custom = { from: '', to: '' },
) => {
  const today = startOfDay(now);
  const end = (start: number, days = 1) => start + days * DAY_MS - 1;
  switch (day) {
    case 'yesterday':
      return { from: today - DAY_MS, to: end(today - DAY_MS) };
    case 'tomorrow':
      return { from: today + DAY_MS, to: end(today + DAY_MS) };
    case 'last7':
      return { from: today - 6 * DAY_MS, to: end(today) };
    case 'next7':
      return { from: today, to: end(today, 7) };
    case 'custom': {
      const from = dayOf(custom.from, today);
      return { from, to: end(Math.max(dayOf(custom.to, from), from)) };
    }
    default:
      return { from: today, to: end(today) };
  }
};

// Aujourd'hui et hier sont lus d'avance ; les autres périodes se lisent à la demande.
type PreloadedDay = 'today' | 'yesterday';
const isPreloaded = (day: StockDay): day is PreloadedDay =>
  day === 'today' || day === 'yesterday';

// « Ce qu'il faut sortir » : les commandes du tableau pas encore remises sont à sortir, les remises sont sorties.
export const createPdsStockStore = (
  id: string,
  gateway: PdsOrdersGateway,
  useOrders: () => ReturnType<typeof usePdsOrdersStore> = usePdsOrdersStore,
) =>
  defineStore(id, () => {
    const orders = useOrders();
    const day = ref<StockDay>('today');
    const customFrom = ref('');
    const customTo = ref('');
    // Tranche horaire de retrait (« HH:MM »), vide = sans borne.
    const hourFrom = ref('');
    const hourTo = ref('');
    const fetched = ref<PdsOrder[]>([]);
    const delivered = ref<Record<PreloadedDay, PdsOrder[]>>({
      today: [],
      yesterday: [],
    });
    const status = ref<'idle' | 'loading' | 'ready' | 'error'>('idle');

    const rangeOf = (which: StockDay) =>
      dayBounds(orders.now, which, {
        from: customFrom.value,
        to: customTo.value,
      });
    const range = computed(() => rangeOf(day.value));
    const multiDay = computed(
      () => range.value.to - range.value.from >= DAY_MS,
    );
    // Une période entièrement passée est close : plus rien à sortir, seulement ce qui a été remis.
    const closed = (which: StockDay) =>
      rangeOf(which).to < startOfDay(orders.now);

    const inHours = (order: PdsOrder) => {
      const from = minutesOf(hourFrom.value);
      const to = minutesOf(hourTo.value);
      const due = new Date(order.dueAt);
      const minutes = due.getHours() * 60 + due.getMinutes();
      return (
        (from === null || minutes >= from) && (to === null || minutes <= to)
      );
    };

    const ofDay = (which: StockDay) => {
      const { from, to } = rangeOf(which);
      if (!isPreloaded(which)) {
        const onBoard = orders.orders.filter(
          (o) => o.dueAt >= from && o.dueAt <= to,
        );
        const known = new Set(onBoard.map((o) => o.id));
        return [...onBoard, ...fetched.value.filter((o) => !known.has(o.id))];
      }
      // Aujourd'hui garde aussi ce qui est resté en retard des jours d'avant : c'est encore à sortir.
      const onBoard = orders.orders.filter(
        (o) => o.dueAt <= to && (which === 'today' || o.dueAt >= from),
      );
      const known = new Set(onBoard.map((o) => o.id));
      // Le tableau fait foi pour ce qu'il connaît ; le serveur complète avec les commandes déjà remises.
      return [
        ...onBoard,
        ...delivered.value[which].filter((o) => !known.has(o.id)),
      ];
    };

    const zonesOf = (which: StockDay, withHours = false) => {
      const all = ofDay(which);
      const inPeriod = withHours ? all.filter(inHours) : all;
      return buildStockZones(
        closed(which) ? inPeriod.filter((o) => o.stage === 'handed') : inPeriod,
        orders.now,
        !isPreloaded(which) && multiDay.value,
      );
    };

    const zones = computed(() => zonesOf(day.value, true));
    const todayTodoCount = computed(
      () =>
        zonesOf('today')
          .flatMap((zone) => zone.lines)
          .filter((line) => !line.done).length,
    );

    let latestFetch = 0;
    const fetchPeriod = async () => {
      if (isPreloaded(day.value)) return;
      const mine = ++latestFetch;
      status.value = 'loading';
      try {
        const { from, to } = range.value;
        const found = await gateway.loadOrdersDue(from, to);
        if (mine !== latestFetch) return;
        fetched.value = found;
        status.value = 'ready';
      } catch {
        if (mine === latestFetch) status.value = 'error';
      }
    };

    watch([day, customFrom, customTo], () => void fetchPeriod());

    const load = async () => {
      status.value = 'loading';
      try {
        const [today, yesterday] = await Promise.all(
          (['today', 'yesterday'] as const).map((which) => {
            const { from, to } = dayBounds(Date.now(), which);
            return gateway.loadDeliveredOrders(from, to);
          }),
        );
        delivered.value = { today, yesterday };
        status.value = 'ready';
      } catch {
        status.value = 'error';
        return;
      }
      await fetchPeriod();
    };

    return {
      day,
      customFrom,
      customTo,
      hourFrom,
      hourTo,
      range,
      multiDay,
      zones,
      status,
      todayTodoCount,
      load,
    };
  });

export const usePdsStockStore = createPdsStockStore(
  'pds-stock',
  resolveGateway(),
);
